/**
 * Section: Recently Viewed Products
 * -----------------------------------------------------------------------------
 * - A carousel of 'Recently Viewed' products cards.
 */

(() => {
  const RECENTLY_VIEWED_STORAGE_KEY = 'recentlyViewed';
  const RECENTLY_VIEWED_ELEMENT = 'recently-viewed';
  const CSS_MOBILE_HIDDEN = 'mobile-hide';
  const CSS_DESKTOP_HIDDEN = 'desktop-hide';

  const selectors = {
    slider: '[js-recently-viewed]',
    productDetails: '[js-product-details]',
    featuredProductItem: '[js-featured-product]',
    carouselSlider: '[js-carousel-slider]',
  };

  const attributes = {
    sectionProducts: 'data-section-products',
  }

  class RecentlyViewed extends HTMLElement {
    constructor() {
      super();
      this.productsSection = this.attributes[attributes.sectionProducts].value;

      this.setDefaultState();
    }

    /**
     * Invokes each time the custom element is appended into a document-connected
     * element
     * - this will happen each time the node is moved and may happen before
     * the element's contents have been fully parsed.
     */
    connectedCallback() {
      this.setProducts();
    }

    /**
     * Set the default state.
     */
    setDefaultState() {
      this.mobileHide = this.parseAttributeValue(
        this.getAttribute('mobile-hide')
      );
      this.desktopHide = this.parseAttributeValue(
        this.getAttribute('desktop-hide')
      );
      this.productLimit = Number(
        this.parseAttributeValue(this.getAttribute('product-limit'))
      );
    }

    /**
     * Set the products that will be displayed in the component.
     */
    async setProducts() {
      try {
        let products = JSON.parse(
          sessionStorage.getItem(RECENTLY_VIEWED_STORAGE_KEY) || '[]'
        );

        /**
         * If on the PDP, do not show the current product in the carousel.
         */
        if (window.location.href.includes('/products/')) {
          // Extract the product handle from the URL using a regular expression
          const regex = /\/products\/([^/?]+)/;
          const match = window.location.href.match(regex);

          if (!match || !match[1]) {
            return;
          }

          const productHandle = match[1];

          products = products.filter((product) => {
            return product.handle !== productHandle;
          });
        }

        const containerElement = document.querySelector('.js-search-extra-content');

        if (!products.length) {
          containerElement?.classList.remove('hidden'); // Add class to hide the element
          this.destroyComponent();
          return;
        } else {
          containerElement?.classList.add('hidden'); // Remove class to show the element
        }

        const queries = products.map((product) => {
          return this.getProductHTML(product.handle);
        });

        const response = await Promise.allSettled(queries);

        const productsNodes = response
          .map((promise) => {
            /**
             * Filter out unavailable products.
             */
            if (promise.status !== 'fulfilled' || !promise.value) {
              return false;
            }

            const productNode = promise.value;
            const productDetails = productNode?.querySelector(
              selectors.productDetails
            );

            if (!productDetails) {
              return promise.value;
            }

            this.mobileHide
              ? productDetails.classList.add(CSS_MOBILE_HIDDEN)
              : productDetails.classList.remove(CSS_MOBILE_HIDDEN);

            this.desktopHide
              ? productDetails.classList.add(CSS_DESKTOP_HIDDEN)
              : productDetails.classList.remove(CSS_DESKTOP_HIDDEN);

            return productNode;
          })
          .filter(Boolean);

        const sliderNode = this.querySelector(selectors.slider);

        if (!sliderNode) {
          throw new Error(
            `Error finding "${selectors.slider}" node in Recently Viewed section`
          );
        }

        productsNodes.forEach((node, index) => {
          if (index > this.productLimit) {
            return;
          }

          sliderNode.append(node);
        });

        this.initSlider();
      } catch (error) {
        console.error(`Error fetching Recently Viewed products ${error}`);
      }
    }

    /**
     * Fetches the HTML content of the featured product section.
     * @param {string} handle - The handle of the product.
     * @returns {Promise<Element|false>} - The HTML element of the product card
     * or `false` if there's any error.
     */
    async getProductHTML(handle) {
      if (!handle) {
        return false;
      }

      try {
        // Fetch the section
        const response = await fetch(
          `${window.Shopify.routes.root}products/${handle}?sections=${this.productsSection}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch "${this.productsSection}" section.`
          );
        }

        const data = await response.json();

        // Check if the section exists
        if (!data || !data[this.productsSection]) {
          return false;
        }

        const sectionHTML = data[this.productsSection];
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionHTML, 'text/html');
        const featuredProduct = doc.querySelector(
          selectors.featuredProductItem
        );

        if (!featuredProduct) {
          return false;
        }

        return featuredProduct;
      } catch (error) {
        console.error(
          `Error fetching "${this.productsSection}" section`,
          error
        );

        return false;
      }
    }

    /**
     * Initializes the Slick slider for the element.
     * @fires `theme:init-slider`
     */
    initSlider() {
      const slider = this.querySelector(selectors.slider);

      if (!slider) {
        return;
      }

      const options = this.parseAttributeValue(
        slider.getAttribute('data-slider-options')
      );

      if (options) {
        slider.classList.add('slick', 'inline-slick');

        document.dispatchEvent(
          new CustomEvent('theme:init-slider', {
            detail: {
              slickHolder: slider.getAttribute('id'),
              slickOptions: options,
            },
          })
        );
      }
    }

    /**
     * Remove the element.
     */
    destroyComponent() {
      this.remove();
    }

    /**
     * Parse and format the attribute value based on its type.
     * @param {string} value - The attribute value to be parsed.
     * @returns {*} - The parsed and formatted attribute value.
     */
    parseAttributeValue(value) {
      try {
        // Attempt to parse the value as JSON
        const parsedValue = JSON.parse(value);

        // If not a boolean or JSON, handle it as a string
        return typeof parsedValue === 'object' ||
          typeof parsedValue === 'boolean'
          ? parsedValue
          : value;
      } catch (error) {
        // If JSON parse fails, handle it as a string
        console.error(
          `Unable to parse slider options in Recently Viewed section ${error}`
        );

        return value;
      }
    }
  }

  /**
   * Register element.
   */
  if (
    'customElements' in window &&
    !customElements.get(RECENTLY_VIEWED_ELEMENT)
  ) {
    customElements.define(RECENTLY_VIEWED_ELEMENT, RecentlyViewed);
  }
})();
